'use client'

import { useState } from 'react'
import { ArrowRight } from '@phosphor-icons/react'
import { isUsingKeyboard, useKeyboardModeTracker } from '@/lib/seasons/useKeyboardMode'

interface InputSubmitProps {
  id: string
  label: string
  type?: string
  required?: boolean
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  disabled?: boolean
  loading?: boolean
  submitLabel?: string
}

export function InputSubmit({
  id,
  label,
  type = 'email',
  required = false,
  value,
  onChange,
  disabled = false,
  loading = false,
  submitLabel = 'Submit',
}: InputSubmitProps) {
  useKeyboardModeTracker()

  const [focused, setFocused] = useState(false)
  const [active, setActive] = useState(Boolean(value))

  const handleFocus = () => {
    if (isUsingKeyboard()) {
      setFocused(true)
      setActive(false)
    } else {
      setFocused(false)
      setActive(true)
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false)
    setActive(e.target.value.trim().length > 0)
  }

  const rootClassName = [
    'input-root',
    'input-submit-root',
    focused && 'is-focused',
    active && !focused && 'is-active',
    disabled && 'is-disabled',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClassName}>
      <div
        className="input-container"
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          // Remove default padding — inner text div owns vertical + left; button owns right inset
          padding: 0,
        }}
        onClick={(e) => {
          if (disabled) return
          if ((e.target as HTMLElement).closest('button')) return
          document.getElementById(id)?.focus()
        }}
      >
        {/* Label + field column — mirrors .input-container stacked-label padding */}
        <div
          className="input-submit-text"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--seasons-space-2)',
            // 14px/18px mirrors --seasons-spacing-input (optical, intentionally off 4px grid)
            // paddingRight 14px + button margin 4px = 18px → matches paddingLeft for symmetry
            paddingTop: '14px',
            paddingBottom: '14px',
            paddingLeft: '18px',
            paddingRight: '14px',
          }}
        >
          <label className="input-label" htmlFor={id}>
            {label}
            {required && (
              <span className="input-label__required" aria-hidden="true">*</span>
            )}
          </label>
          <input
            id={id}
            type={type}
            className="input-field"
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={required}
            aria-required={required || undefined}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {/* Arrow submit button — inset from container edge */}
        <button
          type="submit"
          aria-label={submitLabel}
          disabled={disabled || loading}
          className="btn-icon-only btn-icon-only--md btn-icon-only--midnight"
          style={{ flexShrink: 0, margin: 'var(--seasons-space-1) var(--seasons-space-3) var(--seasons-space-1) var(--seasons-space-1)' }}
        >
          <ArrowRight size={18} weight="bold" />
        </button>
      </div>
    </div>
  )
}
